import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanBanner } from '@/components';
import { PageStatus } from '@/types/store';
import { useDefaultStore } from '@/stores/defaultStore';
import { useUser } from '@/queries/useUser';
import { formatCurrency, getCurrencySymbol } from '@/lib/currency';
import { stanNotify } from '@/lib/stanNotify';
import IcInfo from '@/assets/icons/ic-info-circle.svg';
import { DeleteFunnelPageModal } from '../modals/DeleteFunnelPageModal';
import { FunnelRoutingSection } from '../routing/FunnelRoutingSection';
import { RoutingWarningModal } from '../routing/RoutingWarningModal';
import type { FunnelPageItem, FunnelPageProductInfo, FunnelPageRouting } from '../../../types';
import { DEFAULT_TOP_BANNER_TEXT } from '../../../constants/funnels.constants';
import {
    collectRoutingWarnings,
    getFunnelPageRelationships,
    getFunnelPageRoleDetails,
    getFunnelProductEligibility,
    type RoutingWarning,
} from '../../../logic';
import { type FunnelPageEditorFormValues, funnelPageEditorSchema } from '../../../validations/funnelPageEditor';
import type { PublishFunnelPagePayload } from '../../../hooks/useFunnelPublishPage';
import { FunnelPageEditorHeader } from './FunnelPageEditorHeader';
import { ProductPicker } from './ProductPicker';
import { TopBannerSection } from './TopBannerSection';
import { FunnelOverridesSection } from './FunnelOverridesSection';
import { PublishFooter } from './PublishFooter';

interface EditableFunnelPageCardProps {
    page: FunnelPageItem;
    stepNumber: number;
    isDraft: boolean;
    isPublishing: boolean;
    isDeleting: boolean;
    isHiding: boolean;
    isDetaching: boolean;
    isStepUnlocked: boolean;
    allPages: FunnelPageItem[];
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    onPublish: (payload: PublishFunnelPagePayload) => void;
    onDelete: (pageId: number) => void;
    onHide: (pageId: number) => void;
    onDetachFromParent: (pageId: number) => void;
}

function routingsEqual(a: FunnelPageRouting | null, b: FunnelPageRouting | null): boolean {
    const targetEq = (x: { page_id: number } | null | undefined, y: { page_id: number } | null | undefined) =>
        (x?.page_id ?? null) === (y?.page_id ?? null);
    return targetEq(a?.on_purchase, b?.on_purchase) && targetEq(a?.on_skip, b?.on_skip);
}

/**
 * Normalize a user-entered funnel price string into either a number (cents-compatible
 * float), null (explicit clear), or undefined (leave server value untouched).
 *
 * - Empty string → null (clear override).
 * - Unparseable / negative → undefined (don't send, don't clobber).
 * - Valid positive number → that number.
 */
function parseFunnelPriceInput(input: string): number | null | undefined {
    const trimmed = input.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return undefined;
    return parsed;
}

export const EditableFunnelPageCard = ({
    page,
    stepNumber,
    isDraft,
    isPublishing,
    isDeleting,
    isHiding,
    isDetaching,
    isStepUnlocked,
    allPages,
    productInfoByPageId,
    onPublish,
    onDelete,
    onHide,
    onDetachFromParent,
}: EditableFunnelPageCardProps) => {
    const { store: linksiteStore } = useDefaultStore();
    const { user } = useUser();
    const currencySymbol = getCurrencySymbol(user?.data?.currency ?? 'USD');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingWarnings, setPendingWarnings] = useState<RoutingWarning[] | null>(null);
    const [unsupportedProductReasons, setUnsupportedProductReasons] = useState<string[] | null>(null);

    const relationship = getFunnelPageRelationships(allPages).get(page.page_id);
    const roleDetails = getFunnelPageRoleDetails(stepNumber - 1, relationship);
    // Top banner only shows on funnel steps after the first
    const showTopBanner = stepNumber > 1;

    const initialFunnelPriceInput = typeof page.funnelPrice === 'number' ? String(page.funnelPrice) : '';

    const { control, watch, setValue, formState, reset } = useForm<FunnelPageEditorFormValues>({
        resolver: zodResolver(funnelPageEditorSchema),
        defaultValues: {
            referencePageId: page.referencePageId ?? null,
            topBannerText: page.topBannerText ?? (showTopBanner ? DEFAULT_TOP_BANNER_TEXT : ''),
            funnelPriceInput: initialFunnelPriceInput,
            funnelCtaText: page.funnelCtaText ?? '',
            routing: page.routing ?? null,
        },
    });

    useEffect(() => {
        reset({
            referencePageId: page.referencePageId ?? null,
            topBannerText: page.topBannerText ?? (showTopBanner ? DEFAULT_TOP_BANNER_TEXT : ''),
            funnelPriceInput: typeof page.funnelPrice === 'number' ? String(page.funnelPrice) : '',
            funnelCtaText: page.funnelCtaText ?? '',
            routing: page.routing ?? null,
        });
    }, [
        page.referencePageId,
        page.topBannerText,
        page.funnelPrice,
        page.funnelCtaText,
        page.routing,
        reset,
        showTopBanner,
    ]);

    const referencePageId = watch('referencePageId');
    const topBannerText = watch('topBannerText');
    const funnelPriceInput = watch('funnelPriceInput');
    const funnelCtaText = watch('funnelCtaText');
    const routing = watch('routing');

    // Resolve the selected linksite page (used both for the product preview price override
    // and for pulling the product's default CTA text into the overrides section).
    const selectedLinksitePage = referencePageId
        ? (linksiteStore?.pages?.find(p => p.page_id === referencePageId) ?? null)
        : null;
    const selectedProductEligibility = useMemo(
        () => (selectedLinksitePage ? getFunnelProductEligibility(selectedLinksitePage) : null),
        [selectedLinksitePage]
    );
    const defaultCtaText = selectedLinksitePage?.data?.button?.button_text ?? '';
    const storePriceAmount = selectedLinksitePage?.data?.product?.price?.amount;
    const storePriceCurrency = selectedLinksitePage?.data?.product?.price?.currency ?? user?.data?.currency ?? 'USD';
    // Price override hidden — see memory-bank/funnels/contextual-pricing-findings.md. Flip to re-enable.
    const PRICE_OVERRIDE_ENABLED = false;
    const hasPaidStorePrice = PRICE_OVERRIDE_ENABLED && typeof storePriceAmount === 'number' && storePriceAmount > 0;
    const storePriceLabel = hasPaidStorePrice ? formatCurrency(storePriceAmount, storePriceCurrency) : null;

    // Live preview of the price override as the user types — falls back to null so
    // SelectedProductPreview renders the store price untouched.
    const parsedFunnelPrice = parseFunnelPriceInput(funnelPriceInput);
    const funnelPricePreview = typeof parsedFunnelPrice === 'number' ? parsedFunnelPrice : null;

    const routingChanged = !routingsEqual(routing, page.routing ?? null);
    const hasPendingChange = isDraft || formState.isDirty;

    useEffect(() => {
        if (!selectedProductEligibility) return;

        if (selectedProductEligibility.eligible) {
            setUnsupportedProductReasons(null);
            return;
        }

        setUnsupportedProductReasons(selectedProductEligibility.reasons);
        setValue('referencePageId', null, { shouldDirty: true });
    }, [selectedProductEligibility, setValue]);

    const performPublish = useCallback(
        (values: FunnelPageEditorFormValues) => {
            if (values.referencePageId === null) return;
            // Resolve the selected product title from the linksite store at publish time
            // so we don't need to carry it in form state.
            const match = linksiteStore?.pages?.find(p => p.page_id === values.referencePageId);
            const eligibility = match ? getFunnelProductEligibility(match) : null;
            if (!match || !eligibility?.eligible) {
                stanNotify({
                    type: 'error',
                    title: 'Product is not supported in funnels',
                    text: eligibility?.reasons.join(' ') || 'Pick a different product to publish this step.',
                });
                return;
            }
            const productTitle = match?.data?.product?.title?.trim() || 'Untitled Product';

            // Only send a price override for paid products
            const funnelPricePayload = hasPaidStorePrice ? parseFunnelPriceInput(values.funnelPriceInput) : undefined;

            onPublish({
                pageId: page.page_id,
                referencePageId: values.referencePageId,
                productTitle,
                order: page.order,
                rawOrder: page.rawOrder,
                topBannerText: showTopBanner ? values.topBannerText : undefined,
                funnelPrice: funnelPricePayload,
                funnelCtaText: values.funnelCtaText,
                routing: values.routing,
            });

            reset(values, { keepValues: true });
        },
        [linksiteStore, onPublish, page.page_id, page.order, page.rawOrder, showTopBanner, hasPaidStorePrice, reset]
    );

    const handlePublish = useCallback(() => {
        const values: FunnelPageEditorFormValues = {
            referencePageId,
            topBannerText,
            funnelPriceInput,
            funnelCtaText,
            routing,
        };
        if (values.referencePageId === null) return;
        if (routingChanged) {
            const warnings = collectRoutingWarnings(allPages, page.page_id, routing);
            if (warnings.length > 0) {
                setPendingWarnings(warnings);
                return;
            }
        }
        performPublish(values);
    }, [
        referencePageId,
        topBannerText,
        funnelPriceInput,
        funnelCtaText,
        routing,
        routingChanged,
        allPages,
        page.page_id,
        performPublish,
    ]);

    const handleConfirmWarning = useCallback(() => {
        setPendingWarnings(null);
        performPublish({ referencePageId, topBannerText, funnelPriceInput, funnelCtaText, routing });
    }, [performPublish, referencePageId, topBannerText, funnelPriceInput, funnelCtaText, routing]);

    const canPublish = !!referencePageId && !isPublishing && isStepUnlocked;

    // Only offer Hide on a page that's actually live in the funnel with no unsaved edits —
    // matches Vue's funnel v1 behavior where Hide simply flips a published page to draft.
    const canHide = page.status === PageStatus.PUBLISHED && !hasPendingChange && !isHiding && !isPublishing;

    const confirmDelete = () => {
        onDelete(page.page_id);
        setShowDeleteModal(false);
    };

    // Section numbering is built up dynamically — top banner (step 2+) and funnel price
    // (paid products only) are both optional. CTA override is always shown once a product
    // is selected; routing always closes out the list.
    let sectionNumber = 0;
    const topBannerSectionNumber = showTopBanner ? ++sectionNumber : null;
    const productSectionNumber = ++sectionNumber;
    const priceSectionNumber = hasPaidStorePrice ? ++sectionNumber : null;
    const ctaSectionNumber = ++sectionNumber;
    const routingSectionNumber = ++sectionNumber;

    return (
        <>
            <div className='bg-canvas shadow-stan animate-in fade-in slide-in-from-bottom-1 flex flex-1 flex-col gap-6 rounded-2xl p-4 duration-200 ease-out md:gap-8 md:p-7'>
                <FunnelPageEditorHeader
                    title={roleDetails.title}
                    description={roleDetails.description}
                    onRemove={() => (isDraft ? onDelete(page.page_id) : setShowDeleteModal(true))}
                    canRemove={stepNumber > 1}
                />

                {!isStepUnlocked && (
                    <StanBanner
                        variant='info'
                        icon={IcInfo}
                        title='Finish earlier steps first'
                        subtitle='Pick a product for every earlier page before setting one up for this step.'
                    />
                )}

                {topBannerSectionNumber !== null && (
                    <TopBannerSection
                        sectionNumber={topBannerSectionNumber}
                        value={topBannerText}
                        onChange={next => setValue('topBannerText', next, { shouldDirty: true })}
                        disabled={!isStepUnlocked}
                    />
                )}

                <ProductPicker
                    pageId={page.page_id}
                    sectionNumber={productSectionNumber}
                    description={roleDetails.productSelectorDescription}
                    selectedPageId={referencePageId}
                    funnelPriceOverride={funnelPricePreview}
                    disabled={!isStepUnlocked}
                    onSelect={id => setValue('referencePageId', id, { shouldDirty: true })}
                />

                {unsupportedProductReasons && (
                    <StanBanner
                        variant='warning'
                        icon={IcInfo}
                        title='This product was removed from the funnel page'
                        subtitle={`Pick a different product to publish this step. ${unsupportedProductReasons.join(' ')}`}
                    />
                )}

                {referencePageId !== null && (
                    <FunnelOverridesSection
                        pageId={page.page_id}
                        priceSectionNumber={priceSectionNumber}
                        ctaSectionNumber={ctaSectionNumber}
                        currencySymbol={currencySymbol}
                        storePriceLabel={storePriceLabel}
                        funnelPriceInput={funnelPriceInput}
                        funnelCtaText={funnelCtaText}
                        defaultCtaText={defaultCtaText}
                        disabled={!isStepUnlocked}
                        onFunnelPriceChange={next => setValue('funnelPriceInput', next, { shouldDirty: true })}
                        onFunnelCtaTextChange={next => setValue('funnelCtaText', next, { shouldDirty: true })}
                    />
                )}

                {referencePageId !== null && (
                    <Controller
                        control={control}
                        name='routing'
                        render={({ field }) => (
                            <FunnelRoutingSection
                                page={page}
                                stepNumber={stepNumber}
                                sectionNumber={routingSectionNumber}
                                allPages={allPages}
                                productInfoByPageId={productInfoByPageId}
                                routing={field.value}
                                onChange={next => field.onChange(next)}
                                onDetachFromParent={() => onDetachFromParent(page.page_id)}
                                isDetaching={isDetaching}
                                disabled={!isStepUnlocked}
                            />
                        )}
                    />
                )}

                <PublishFooter
                    status={page.status}
                    hasPendingChange={hasPendingChange}
                    canPublish={canPublish}
                    canHide={canHide}
                    isPublishing={isPublishing}
                    isHiding={isHiding}
                    onPublish={handlePublish}
                    onHide={() => onHide(page.page_id)}
                />
            </div>

            <DeleteFunnelPageModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                pageTitle={page.title || `Page ${stepNumber}`}
                isDeleting={isDeleting}
                onConfirm={confirmDelete}
            />

            <RoutingWarningModal
                isOpen={pendingWarnings !== null}
                warnings={pendingWarnings ?? []}
                isPublishing={isPublishing}
                onClose={() => setPendingWarnings(null)}
                onConfirm={handleConfirmWarning}
            />
        </>
    );
};
